/**
 * QR da campanha por cima do criativo. Gerado no próprio aparelho (funciona offline: o link é fixo por tela e só o
 * celular de quem escaneia precisa de internet). Desenhado como SVG de módulos, sem HTML injetado.
 */
import qrcode from 'qrcode-generator'
import { useMemo } from 'react'
import type { QrOverlayDTO } from '@/types/player.types'

/** Zona de silêncio exigida pela norma do QR (ISO/IEC 18004): 4 módulos de margem branca. */
const QUIET_ZONE = 4
/** Afastamento das bordas da tela, em % do menor lado (o painel usa o mesmo na prévia). */
const EDGE_VMIN = 3

/** Caminho SVG com um quadrado por módulo escuro, já deslocado pela zona de silêncio. */
export function qrModulesPath(text: string): { path: string; size: number } {
  // Correção 'M' (15%): aguenta reflexo e leitura a distância sem deixar o código denso demais.
  const qr = qrcode(0, 'M')
  qr.addData(text)
  qr.make()
  const count = qr.getModuleCount()
  let path = ''
  for (let r = 0; r < count; r++) {
    for (let c = 0; c < count; c++) {
      if (qr.isDark(r, c)) path += `M${c + QUIET_ZONE} ${r + QUIET_ZONE}h1v1h-1z`
    }
  }
  return { path, size: count + QUIET_ZONE * 2 }
}

const POSITION_STYLE: Record<QrOverlayDTO['position'], React.CSSProperties> = {
  top_left: { top: `${EDGE_VMIN}vmin`, left: `${EDGE_VMIN}vmin` },
  top_right: { top: `${EDGE_VMIN}vmin`, right: `${EDGE_VMIN}vmin` },
  bottom_left: { bottom: `${EDGE_VMIN}vmin`, left: `${EDGE_VMIN}vmin` },
  bottom_right: { bottom: `${EDGE_VMIN}vmin`, right: `${EDGE_VMIN}vmin` }
}

export function QrOverlay({ qr }: { qr: QrOverlayDTO }) {
  const { path, size } = useMemo(() => qrModulesPath(qr.url), [qr.url])
  const side = `${qr.sizePct}vmin`

  return (
    <div
      data-testid="qr-overlay"
      style={{
        position: 'absolute',
        ...POSITION_STYLE[qr.position],
        width: side,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: `calc(${side} * 0.04)`,
        zIndex: 10,
        pointerEvents: 'none'
      }}
    >
      <svg
        viewBox={`0 0 ${size} ${size}`}
        width="100%"
        shapeRendering="crispEdges"
        role="img"
        aria-label="QR code"
        style={{ display: 'block', borderRadius: `calc(${side} * 0.04)` }}
      >
        <rect width={size} height={size} fill="#fff" />
        <path d={path} fill="#000" />
      </svg>
      {qr.callToAction && (
        <span
          style={{
            maxWidth: '100%',
            padding: `calc(${side} * 0.02) calc(${side} * 0.06)`,
            borderRadius: `calc(${side} * 0.04)`,
            background: 'rgba(0, 0, 0, 0.7)',
            color: '#fff',
            fontFamily: 'system-ui, sans-serif',
            fontWeight: 600,
            fontSize: `calc(${side} * 0.11)`,
            lineHeight: 1.2,
            textAlign: 'center',
            overflowWrap: 'anywhere'
          }}
        >
          {qr.callToAction}
        </span>
      )}
    </div>
  )
}
